import { Delete as DeleteIcon } from "@mui/icons-material";
import {
	Card,
	CardContent,
	CardMedia,
	Chip,
	Grid,
	IconButton,
	PaperProps,
	styled,
	Typography,
} from "@mui/material";
import { blue, green, orange, pink, purple } from "@mui/material/colors";
import { types } from "api";
import { getPreviewUrl } from "api/base";
import clsx from "clsx";
import { forwardRef } from "react";

const PREFIX = "SimpleActivityCard";

const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	separator: `${PREFIX}-separator`,
	title: `${PREFIX}-title`,
	chip: `${PREFIX}-chip`,
	quiz: `${PREFIX}-quiz`,
	animation: `${PREFIX}-animation`,
	game: `${PREFIX}-game`,
	book: `${PREFIX}-book`,
	song: `${PREFIX}-song`,
};

const StyledPaper = styled(Card)(({ theme }) => ({
	minWidth: "200px",
	maxWidth: "340px",
	marginLeft: theme.spacing(1),
	marginBottom: theme.spacing(1),
	borderRadius: 4,

	[`& .${classes.button}`]: {
		maxWidth: 20,
		minWidth: 20,
		maxHeight: 20,
		minHeight: 20,
		marginLeft: 20,
	},
	[`& .${classes.separator}`]: {
		height: theme.spacing(1),
		margin: 0,
	},
	[`& .${classes.title}`]: {
		marginTop: theme.spacing(0.5),
	},
	[`& .${classes.chip}`]: {
		color: theme.palette.background.paper,
		marginTop: theme.spacing(1),
	},
	[`& .${classes.song}`]: {
		backgroundColor: purple[400],
	},
	[`& .${classes.game}`]: {
		backgroundColor: orange[400],
	},
	[`& .${classes.quiz}`]: {
		backgroundColor: green[400],
	},
	[`& .${classes.animation}`]: {
		backgroundColor: blue[400],
	},
	[`& .${classes.book}`]: {
		backgroundColor: pink[400],
	},
}));

export interface SimpleActivityCardProps {
	activity: types.Activity;
	remove: (activity: types.Activity) => void;
	show: boolean;
}

export const SimpleActivityCard = forwardRef(function SimpleActivityCardCore(
	{ activity, remove, show }: SimpleActivityCardProps,
	ref,
): JSX.Element {
	const baseURL = getPreviewUrl(activity.questId);
	const activityTypeClassName = clsx({
		[classes.animation]: activity.activityType === "animation",
		[classes.game]: activity.activityType === "game",
		[classes.book]: activity.activityType === "book",
		[classes.song]: activity.activityType === "song",
		[classes.quiz]: activity.activityType === "quiz",
	});

	return (
		<StyledPaper
			className={classes.root}
			data-testid="simple-card"
			ref={ref as PaperProps["ref"]}
		>
			<CardMedia component="img" image={baseURL} alt="preview" />
			<hr className={clsx(activityTypeClassName, classes.separator)} />

			<CardContent>
				<Grid display="grid" gridTemplateColumns="auto 1fr">
					<Typography variant="h6" className={classes.title}>
						{activity.name}
					</Typography>
					{show && (
						<Grid container justifyContent="flex-end" alignItems="flex-start">
							<IconButton
								aria-label="delete activity"
								onClick={() => remove(activity)}
								data-testid="remove-btn"
								color="error"
							>
								<DeleteIcon />
							</IconButton>
						</Grid>
					)}
				</Grid>

				<Chip
					label={activity.activityType}
					className={clsx(activityTypeClassName, classes.chip)}
				/>
			</CardContent>
		</StyledPaper>
	);
});
